import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import { ReportType } from '../../../../../types';
import { ReportFormValues } from '../reports-form-page';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ViewColumn from '@mui/icons-material/ViewColumn';

function CustomToolbar() {
  return (
    <SharedToolbar noColumn noFilter>
      <Stack flexDirection="row" gap={1}></Stack>
    </SharedToolbar>
  );
}
interface StepSelectColumnsProps {
  control: Control<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  selectedReportType: ReportType | null;
  errors: FieldErrors<ReportFormValues>;
  apiAccessGranted: boolean;
  cloudIdentityReportTypes: string[];

  dialogType: 'create' | 'edit';
}

interface ColumnItem {
  columnName: string;
  columnDescription?: string;
  valueName: string;
  name: string;
  isCanned: boolean;
  useInReport: boolean;
  requiredInReport: boolean;
  requiredInBulkOps: boolean;
  showInColumnPicker: boolean;
  type?: string;
}

export default function StepSelectColumns({
  watch,
  control,
  setValue,
  selectedReportType,
  errors,
  apiAccessGranted,
  cloudIdentityReportTypes,
  dialogType,
}: StepSelectColumnsProps) {
  const { features } = useUserSettings();
  const driveLabelsEnabled = isFeatureEnabled('driveLabelsOps', features);

  const watchedColumns = watch('columns');

  const selectedColumns = useMemo(() => {
    return watchedColumns ?? [];
  }, [watchedColumns]);

  const reportType = watch('reportType') || '';

  const possibleColumns = (selectedReportType?.columns || []).filter(
    (col) => driveLabelsEnabled || col.name !== 'driveLabels'
  );

  const isColumnDisabled = (row: ColumnItem) => {
    // Check if this is a "Created Date" column that should be conditionally disabled
    if (row.columnName === 'Created Date') {
      const shouldDisable =
        !apiAccessGranted && cloudIdentityReportTypes.includes(reportType);
      return shouldDisable;
    }

    // Check other always-disabled column
    return row.requiredInReport;
  };

  // Helper function to filter out disabled columns
  const filterDisabledColumns = (
    columns: typeof possibleColumns
  ): typeof possibleColumns => {
    return columns.filter((column) => !isColumnDisabled(column));
  };

  // Initialize selectedRows, filtering out any disabled columns
  const getInitialSelectedRows = () => {
    const initial = dialogType === 'create' ? possibleColumns : selectedColumns;
    return filterDisabledColumns(initial);
  };

  const [selectedRows, setSelectedRows] = useState(getInitialSelectedRows);

  // Sync selectedRows with filteredSelectedColumns
  // This handles the case when coming back to this step
  useEffect(() => {
    // Sync selectedRows with the filtered columns
    setSelectedRows(selectedColumns);
  }, [selectedColumns]);

  const unselectableRows = possibleColumns.filter((row) =>
    isColumnDisabled(row)
  );
  const selectableRows = possibleColumns.filter(
    (row) => !isColumnDisabled(row)
  );
  const sortedRows = [...unselectableRows, ...selectableRows];

  const columns: GridColDef<ColumnItem>[] = [
    {
      field: 'columnName',
      headerName: 'Available Columns',
      editable: false,
      sortable: true,
      filterable: false,
      flex: 1,
      renderCell: (params): React.ReactNode => {
        const description = params.row?.columnDescription;
        if (!description) {
          return params.value;
        }

        return (
          <Tooltip arrow title={description}>
            <Box component="span">{params.value}</Box>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
      gap={1}
      alignItems={'left'}
    >
      <Stack alignItems="left" sx={{ minHeight: 0 }}>
        <Typography sx={{ mb: 0.5, flex: 1 }}>
          Optionally provide which columns you would like to have in your
          generated report. All columns are selected by default. Any columns
          that you can not unselect are required in the report.
        </Typography>
      </Stack>
      <Stack alignItems="left" sx={{ flex: 1, minHeight: 0 }}>
        <Controller
          name={'columns'}
          control={control}
          render={() => (
            <Box sx={{ flex: 1, minHeight: 0, p: 0 }}>
              <SharedDataGridClient
                slots={{
                  toolbar: CustomToolbar,
                  ...createGridOverlays('Columns', <ViewColumn />),
                }}
                rows={sortedRows}
                columns={columns}
                getRowId={(row: ColumnItem) => row?.name}
                rowHeight={38}
                checkboxSelection
                hideFooterRowCount
                disableRowSelectionOnClick
                keepNonExistentRowsSelected
                onRowSelectionModelChange={(newModel) => {
                  const selectedIDs = new Set(newModel.ids);
                  const selectedRowData = possibleColumns.filter(
                    (row: ColumnItem) => selectedIDs.has(row.name)
                  );
                  setSelectedRows(selectedRowData);
                  setValue('columns', selectedRowData);
                }}
                selectedRows={selectedRows?.map((row) => row.name)}
                isRowSelectable={(params: GridRowParams) =>
                  !isColumnDisabled(params.row)
                }
              />
            </Box>
          )}
        />
      </Stack>
      {errors.columns ? (
        <FormHelperText error>
          {errors.columns.message ? errors.columns.message : ''}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
